import isArray from 'lodash/isArray';
import isEqual from 'lodash/isEqual';
import { ClassifierData } from 'types/classifiers';
import { isHiddenClassifierValue } from 'utils/classifierUtil';
import { getClassifierText } from 'utils/getTranslations';
import { isNumeric } from 'utils/number.ts';
import {
    FilterCheckboxInForm,
    MainFilterForm,
    MainFilterInForm,
    NOT_NULL_FILTER_SENTINEL,
    NULL_FILTER_SENTINEL,
    QuickFilterCheckboxGroupForm,
    QuickFilterDropdownMultiselectForm,
    QuickFilterForm,
    QuickFilterFormItem,
    RealAndVirtualFilterObject,
    SelectOption,
} from './ListTableTypes.tsx';
import { compositeClause } from './compositeFilter.ts';
import { getSelectorKey } from './getSelectorKey.ts';

export const VIRTUAL_MARKER = '@VIRTUAL@';

export const getFilterOptionsFromClassifier = (
    classifier: ClassifierData | undefined
): { label: string; value: string }[] => {
    if (!classifier) {
        return [];
    }

    return classifier.values
        ? classifier.values
              .filter((type) => !isHiddenClassifierValue(classifier.code, type.code))
              .map((type) => ({
                  label: getClassifierText(classifier, type.code),
                  value: type.code,
                  orderNum: type.orderNum,
              }))
              .sort((a, b) => {
                  // Sort by orderNum first (nulls last), then by label
                  if (a.orderNum != null && b.orderNum != null) {
                      return a.orderNum - b.orderNum;
                  }
                  if (a.orderNum != null) {
                      return -1;
                  }
                  if (b.orderNum != null) {
                      return 1;
                  }
                  return a.label.localeCompare(b.label);
              })
              .map(({ label, value }) => ({ label, value }))
        : [];
};

export const getSortedFilterOptions = <T>(
    getLabel: Extract<keyof T, string> | ((item: T) => string),
    getValue: Extract<keyof T, string> | ((item: T) => string),
    optionElements: T[]
): { label: string; value: string }[] => {
    return optionElements
        .reduce<{ label: string; value: string }[]>((currentArray, currentItem) => {
            const label = typeof getLabel === 'string' ? currentItem[getLabel] : getLabel(currentItem);
            const value = typeof getValue === 'string' ? currentItem[getValue] : getValue(currentItem);
            if (label && value) {
                currentArray.push({ label: String(label), value: String(value) });
            }
            return currentArray;
        }, [])
        .sort((a, b) => (a.label as string).localeCompare(b.label as string));
};

export const mapToFilterOptions = (
    classifierType: string[],
    classifier: ClassifierData | undefined,
    overrideMap?: Record<string, string>
) => {
    return classifierType.map((state) => {
        const label = getClassifierText(classifier, state, overrideMap);
        const value = state;
        const argument = state;
        return { label, value, argument };
    });
};

const reduceRealAndVirtualObjectList = (
    filterParts: RealAndVirtualFilterObject[],
    virtual: boolean | undefined
): RealAndVirtualFilterObject => {
    const result = filterParts.reduce(
        (acc, part) => {
            if (part.realFilter !== undefined) acc.real.push(part.realFilter);
            if (part.virtualFilter !== undefined) acc.virtual.push(part.virtualFilter);
            return acc;
        },
        { real: [] as string[], virtual: [] as string[] }
    );
    return {
        realFilter: result.real.length > 0 ? (virtual ? VIRTUAL_MARKER : '') + result.real.join(';') : undefined,
        virtualFilter: result.virtual.length > 0 ? result.virtual.join(';') : undefined,
    };
};

const getJointFilter = (
    params: string,
    filter: MainFilterInForm,
    mainFilterState: MainFilterForm
): string | undefined => {
    if (filter.virtual) {
        return undefined;
    }
    if (!filter.jointFilter) {
        return params;
    }
    const jointFilter = Object.entries(mainFilterState).find(
        ([, val]) => val.selector === filter.jointFilter?.joinSelector
    )?.[1];

    if (!jointFilter) {
        return params;
    }

    const realAndVirtualFilterObject = rsqlifyMainFilter(jointFilter, mainFilterState, true);
    if (!realAndVirtualFilterObject) {
        return;
    }

    const { realFilter } = realAndVirtualFilterObject;

    const { selectors } = filter.jointFilter;
    const comperator = filter.jointFilter.comperator ?? '=lk=';
    const mappedSelectors = selectors.map((selector) => `${selector}${comperator}${realFilter}`);

    return `(${params};(${mappedSelectors}))`;
};

/**
 * An interval bound as it goes on the wire, or `''` when there is none. The state holds the text
 * typed so far while the drawer is open and a number once restored from the URL, so a bare `-` is not
 * a bound yet and `0` is one: neither a null check nor truthiness can tell.
 */
export const intervalBound = (bound: number | null): string => (isNumeric(String(bound)) ? `${bound}` : '');

/** A text field's "is empty": no value, or a blank one. */
export const emptyTextClause = (selector: string) => `(${selector}=isnull=true,${selector}=='')`;

/** A comparator's "none of" twin: `=positionlk=` → `=notpositionlk=`. */
export const notComparator = (comparator: string) => comparator.replace(/^=(.+)=$/, '=not$1=');

/** The "is empty" clause of a date range or a text field, marked and joined as their value clauses are. */
const emptyClause = (
    group: MainFilterInForm,
    params: string,
    mainFilterState: MainFilterForm
): RealAndVirtualFilterObject => ({
    realFilter: (group.virtual ? VIRTUAL_MARKER : '') + params,
    virtualFilter: getJointFilter(params, group, mainFilterState),
});

export const rsqlifyMainFilter = (
    group: MainFilterInForm,
    mainFilterState: MainFilterForm,
    onlyValue: boolean = false
): RealAndVirtualFilterObject | undefined => {
    const { selector, comperator, type } = group;

    if (type === 'checkboxgroup') {
        const filterArguments = [] as string[];
        const overrides = [] as string[];
        group.filters.forEach((filter: FilterCheckboxInForm) => {
            if (!filter.isSelected) return;
            if (filter.rsqlOverride) {
                overrides.push(filter.rsqlOverride);
            } else {
                filterArguments.push(`${filter.argument}`);
            }
        });
        const standardPart = filterArguments.length
            ? `${selector}${comperator ? comperator : '=in='}${
                  !comperator || group?.multiple ? '(' : ''
              }${filterArguments.join(',')}${!comperator || group?.multiple ? ')' : ''}`
            : '';
        const allParts = [standardPart, ...overrides].filter(Boolean);
        if (allParts.length) {
            const params = allParts.length === 1 ? allParts[0] : `(${allParts.join(',')})`;
            const value = filterArguments.length
                ? `${!comperator || group?.multiple ? '(' : ''}${filterArguments.join(',')}${
                      !comperator || group?.multiple ? ')' : ''
                  }`
                : params;
            const virtualParams = getJointFilter(params, group, mainFilterState);
            return {
                realFilter: onlyValue ? value : (group?.virtual ? VIRTUAL_MARKER : '') + params,
                virtualFilter: virtualParams,
            };
        }
    } else if (type === 'date') {
        if (group.value) {
            const prefix = `${selector}${comperator ? comperator : '=in='}`;
            const value = `${getFormatedDate(group.value)}`;
            const params = prefix + value;
            const virtualParams = getJointFilter(params, group, mainFilterState);
            return {
                realFilter: onlyValue ? value : (group?.virtual ? VIRTUAL_MARKER : '') + params,
                virtualFilter: virtualParams,
            };
        }
    } else if (type === 'offset-date-time') {
        if (group.value) {
            const prefix = `${selector}=lk=`;
            const value = `"${getFormatedDate(group.value)}"`;
            const params = prefix + value;
            const virtualParams = getJointFilter(params, group, mainFilterState);
            return {
                realFilter: onlyValue ? value : (group?.virtual ? VIRTUAL_MARKER : '') + params,
                virtualFilter: virtualParams,
            };
        }
    } else if (type === 'daterange' || type === 'interval') {
        if (type === 'interval' && comperator) {
            const fromVal = intervalBound(group.from);
            const toVal = intervalBound(group.to);
            if (fromVal || toVal) {
                const args: string[] = [];
                if (fromVal) {
                    args.push(fromVal);
                }
                if (toVal) {
                    if (!fromVal) {
                        args.push('""');
                    }
                    args.push(toVal);
                }
                const value = `(${args.join(',')})`;
                const params = `${selector}${comperator}${value}`;
                const virtualParams = getJointFilter(params, group, mainFilterState);
                return {
                    realFilter: onlyValue ? value : params,
                    virtualFilter: virtualParams,
                };
            }
            return undefined;
        }
        if (type === 'daterange' && group.isEmpty && !group.from && !group.to) {
            return emptyClause(group, `${selector}=isnull=true`, mainFilterState);
        }
        const filterParts: RealAndVirtualFilterObject[] = [];
        const from = type === 'interval' ? intervalBound(group.from) : group.from;
        const to = type === 'interval' ? intervalBound(group.to) : group.to;
        if (from) {
            const prefix = `${selector}=ge=`;
            const value = `${type === 'daterange' ? getFormatedDate(from) : from}`;
            const params = prefix + value;
            const virtualParams = getJointFilter(params, group, mainFilterState);
            filterParts.push({ realFilter: onlyValue ? value : params, virtualFilter: virtualParams });
        }
        if (to) {
            const prefix = `${selector}=le=`;
            const value = `${type === 'daterange' ? getFormatedDate(to) : to}`;
            const params = prefix + value;
            const virtualParams = getJointFilter(params, group, mainFilterState);
            filterParts.push({ realFilter: params, virtualFilter: virtualParams });
        }
        return reduceRealAndVirtualObjectList(filterParts, group.virtual);
    } else if (type === 'number') {
        // `0` is a threshold the user typed, not an empty field, so this tests for null rather than
        // truthiness the way the string- and date-valued branches can afford to.
        if (group.value != null) {
            const prefix = `${selector}${comperator ?? '=ge='}`;
            const value = `${group.value}`;
            const params = prefix + value;
            const virtualParams = getJointFilter(params, group, mainFilterState);
            return {
                realFilter: onlyValue ? value : (group?.virtual ? VIRTUAL_MARKER : '') + params,
                virtualFilter: virtualParams,
            };
        }
    } else if (type === 'offset-date-time-range') {
        if (comperator) {
            const fromDate = group.from ? getFormatedDate(group.from) : '';
            const toDate = group.to ? getFormatedDate(group.to) : '';
            if (fromDate || toDate) {
                const prefix = `${selector}${comperator}`;
                const args: string[] = [];
                if (fromDate) {
                    args.push(fromDate);
                }
                if (toDate) {
                    if (!fromDate) {
                        args.push('""');
                    }
                    args.push(toDate);
                }
                const value = `(${args.join(',')})`;
                const params = prefix + value;
                const virtualParams = getJointFilter(params, group, mainFilterState);
                return {
                    realFilter: onlyValue ? value : params,
                    virtualFilter: virtualParams,
                };
            }
            return undefined;
        }
        const filterParts: RealAndVirtualFilterObject[] = [];
        if (group.from) {
            const prefix = `${selector}=ge=`;
            const value = `${getFormattedOffsetDateTime(group.from)}`;
            const params = prefix + value;
            const virtualParams = getJointFilter(params, group, mainFilterState);
            filterParts.push({ realFilter: onlyValue ? value : params, virtualFilter: virtualParams });
        }
        if (group.to) {
            const prefix = `${selector}=le=`;
            const value = `${getFormattedOffsetDateTime(group.to)}`;
            const params = prefix + value;
            const virtualParams = getJointFilter(params, group, mainFilterState);
            filterParts.push({ realFilter: onlyValue ? value : params, virtualFilter: virtualParams });
        }
        return reduceRealAndVirtualObjectList(filterParts, group.virtual);
    } else if (type === 'text') {
        if (group.value && group.value.length) {
            if (!group.multiple) {
                const prefix = `${selector}${comperator ? comperator : '=lk='}`;
                const value = `"${group.value}"`;
                const params = prefix + value;
                const virtualParams = getJointFilter(params, group, mainFilterState);
                return {
                    realFilter: onlyValue ? value : (group?.virtual ? VIRTUAL_MARKER : '') + params,
                    virtualFilter: virtualParams,
                };
            } else {
                const groupValue = isArray(group.value) ? group.value : group.value.replace(/['"]+/g, '').split(',');
                const filterArguments = [] as string[];
                groupValue.forEach((item) => {
                    filterArguments.push(`"${item}"`);
                });

                if (filterArguments.length) {
                    const match = group.matchModes ? group.match : undefined;
                    const prefix = `${selector}${match === 'none' ? notComparator(comperator ?? '=lk=') : (comperator ?? '=lk=')}`;
                    const value = `(${filterArguments.join(',')})`;
                    // Every term its own clause: the list reads them back as "match all".
                    const params =
                        match === 'all'
                            ? filterArguments.map((term) => `${prefix}(${term})`).join(';')
                            : prefix + value;
                    const virtualParams = getJointFilter(params, group, mainFilterState);
                    return {
                        realFilter: onlyValue ? value : (group?.virtual ? VIRTUAL_MARKER : '') + params,
                        virtualFilter: virtualParams,
                    };
                }
            }
        } else if (group.isEmpty) {
            return emptyClause(
                group,
                group.emptyComparator ? `${selector}${group.emptyComparator}false` : emptyTextClause(selector),
                mainFilterState
            );
        } else if (group.isNotEmpty && group.emptyComparator) {
            return emptyClause(group, `${selector}${group.emptyComparator}true`, mainFilterState);
        }
    } else if (type === 'multiselect' || type === 'dynamicmultiselect') {
        const filterArguments = [] as string[];
        let hasNullSentinel = false;
        if (group.selected.some((option) => option.value === NOT_NULL_FILTER_SENTINEL)) {
            const params = group.emptyComparator ? `${selector}${group.emptyComparator}true` : `${selector}=nn=true`;
            return {
                realFilter: onlyValue ? params : (group?.virtual ? VIRTUAL_MARKER : '') + params,
                virtualFilter: getJointFilter(params, group, mainFilterState),
            };
        }
        group.selected.forEach((filter: SelectOption) => {
            if (filter.value === NULL_FILTER_SENTINEL) {
                hasNullSentinel = true;
                return;
            }
            if (type === 'multiselect') {
                filterArguments.push(`${filter.value}`);
            }
            if (type === 'dynamicmultiselect') {
                if (isNumeric(String(filter.value))) {
                    filterArguments.push(`${filter.value}`);
                } else {
                    const value = filter.value as string;
                    filterArguments.push(value.startsWith('"') ? value : `"${value}"`);
                }
            }
        });

        if (filterArguments.length || hasNullSentinel) {
            const { notComparator, neverEmpty } = group.conditions ?? {};
            const defaultOperator = group.isExcluding ? (notComparator ?? '=out=') : '=in=';

            // If we have a custom comparator and we're excluding, try to use the negated version
            let effectiveComparator = comperator;
            if (comperator && group.isExcluding) {
                // Check if comparator follows pattern =operator=
                const match = comperator.match(/^=(.+)=$/);
                if (match) {
                    // Transform =operator= to =notoperator=
                    effectiveComparator = `=not${match[1]}=`;
                }
            }

            let params: string;
            let value: string;

            if (hasNullSentinel && filterArguments.length) {
                const realPart = `${selector}${effectiveComparator || defaultOperator}(${filterArguments.join(',')})`;
                params = `(${realPart},${selector}=isnull=true)`;
                value = params;
            } else if (hasNullSentinel) {
                params = group.emptyComparator ? `${selector}${group.emptyComparator}false` : `${selector}=isnull=true`;
                value = params;
            } else {
                value = `(${filterArguments.join(',')})`;
                params = `${selector}${effectiveComparator || defaultOperator}${value}`;
                // When excluding, also include null values using OR (,) operator — unless the field is
                // never empty, or its own "none of" operator already keeps the cases with no value.
                if (group.isExcluding && !comperator && !notComparator && !neverEmpty) {
                    params = `(${params},${selector}=isnull=true)`;
                }
            }

            const virtualParams = getJointFilter(params, group, mainFilterState);
            return {
                realFilter: onlyValue ? value : (group?.virtual ? VIRTUAL_MARKER : '') + params,
                virtualFilter: virtualParams,
            };
        }
    } else if (type === 'composite') {
        const params = compositeClause(group);
        if (!params) return;
        return {
            realFilter: (group.virtual ? VIRTUAL_MARKER : '') + params,
            virtualFilter: getJointFilter(params, group, mainFilterState),
        };
    } else if (type === 'dropdown' || type === 'dynamicdropdown') {
        if (!group.selected?.value) return;
        const prefix = `${selector}${comperator ? comperator : '=in='}`;
        const value = `(${group.selected?.value})`;
        const params = prefix + value;
        const virtualParams = getJointFilter(params, group, mainFilterState);
        return { realFilter: (group?.virtual ? VIRTUAL_MARKER : '') + params, virtualFilter: virtualParams };
    }
};

const getFormatedDate = (args: string) => {
    const newDate = new Date(args);
    const month = newDate.getMonth() + 1;
    const day = newDate.getDate();
    const monthToDisplay = month > 9 ? month : `0${month}`;
    const dayToDisplay = day > 9 ? day : `0${day}`;

    return `${newDate.getFullYear()}-${monthToDisplay}-${dayToDisplay}`;
};

const getFormattedOffsetDateTime = (args: string): string => {
    return new Date(args).toISOString().replace(/\.\d{3}Z$/, '+00:00');
};

const stripVirtualMarker = (item: string): string =>
    item.startsWith(VIRTUAL_MARKER) ? item.slice(VIRTUAL_MARKER.length) : item;

/**
 * Whether a main filter can take over the clause on a key it shares with a quick filter. Only the
 * types `synchronizeMainFilterStatePure` knows how to drive from a quick control qualify — a main
 * filter the shortcut cannot write to would swallow the shortcut's clause and answer with a stale
 * one of its own. For every other type the quick filter keeps emitting the clause, as it always did.
 */
const canOwnSharedKey = (mainFilter: MainFilterInForm | undefined): boolean =>
    mainFilter?.type === 'checkboxgroup' || mainFilter?.type === 'multiselect';

/**
 * Turns the two filter state machines into the `mf`/`qf`/`search` params. Shared by the URL write in
 * `refetch` and by `compareSearchParams`, so the URL a filter change produces and the URL the state
 * is compared against can never drift apart.
 *
 * A quick filter and a main filter can resolve to the same `selector:comparator` key — the quick one
 * being a shortcut onto some of the values the main control offers (a "Show done" toggle beside the
 * full status multiselect). Emitting both would AND two clauses on one selector down to an empty
 * result, and emitting only the quick one drops every value it cannot express. So the main filter
 * owns the clause on a shared key and the quick control is a projection of it, kept in step by
 * `synchronizeMainFilterStatePure` / `synchronizeQuickFilterState`.
 *
 * That clause still travels in `qf`, the param these lists have always carried it in: `mf`
 * membership is what `mainFilterCount` reads to decide totals auto-fetch, and what `hasUserFilters`
 * counts as a filter the user applied — neither may start firing because a quick shortcut is on.
 */
export const serializeFilterParams = ({
    searchText,
    quickFilterState,
    mainFilterState,
}: {
    searchText: string;
    quickFilterState: QuickFilterForm;
    mainFilterState: MainFilterForm;
}): {
    searchParamObject: Record<string, string>;
    quickFilterParams: string[];
    virtualMainFilterParams: string[];
} => {
    const searchParamObject = {} as Record<string, string>;
    const quickFilterParams = [] as string[];
    const mainFilterParams = [] as string[];
    // Clauses of main filters that share a key with a quick filter. They go out under `qf`, and they
    // reach the request through `virtualMainFilterParams` — so they are kept out of the returned
    // quick list, which the caller appends to that same request filter.
    const sharedKeyFilterParams = [] as string[];
    const virtualMainFilterParams = [] as string[];

    Object.entries(quickFilterState).forEach(([itemKey, filter]: [string, QuickFilterFormItem]) => {
        if (canOwnSharedKey(mainFilterState[itemKey])) {
            return;
        }
        const selector = filter.selector;
        const comperator = filter?.comperator;

        // Handle dropdown-multiselect type
        if ('type' in filter && filter.type === 'dropdown-multiselect') {
            const dropdownFilter = filter as QuickFilterDropdownMultiselectForm;
            if (dropdownFilter.selected && dropdownFilter.selected.length > 0) {
                const filterArguments = dropdownFilter.selected.map((opt) => String(opt.value));
                quickFilterParams.push(`${selector}${comperator ? comperator : '=in='}(${filterArguments.join(',')})`);
            }
        } else {
            // Handle checkbox group type (default)
            const checkboxGroup = filter as QuickFilterCheckboxGroupForm;
            const multiple = checkboxGroup?.multiple ?? false;
            const filterArguments = [] as string[];

            checkboxGroup.filters?.forEach((f: FilterCheckboxInForm) => {
                if (!f.isSelected) return;
                filterArguments.push(`${f.argument}`);
            });

            if (filterArguments.length) {
                quickFilterParams.push(
                    `${selector}${comperator ? comperator : '=in='}${
                        !comperator || multiple ? '(' : ''
                    }${filterArguments.join(',')}${!comperator || multiple ? ')' : ''}`
                );
            }
        }
    });

    Object.values(mainFilterState).forEach((group) => {
        const itemKey = getSelectorKey(group);
        const sharesKeyWithQuickFilter = quickFilterState[itemKey] !== undefined;
        if (sharesKeyWithQuickFilter && !canOwnSharedKey(group)) {
            return;
        }

        const realAndVirtualFilterObject = rsqlifyMainFilter(group, mainFilterState);
        if (!realAndVirtualFilterObject) {
            return;
        }

        const { realFilter, virtualFilter } = realAndVirtualFilterObject;

        if (realFilter) {
            (sharesKeyWithQuickFilter ? sharedKeyFilterParams : mainFilterParams).push(realFilter);
            if (!virtualFilter && !group.virtual) {
                // since virtualFilter is used for the api request params, default it to "realFilter"
                virtualMainFilterParams.push(realFilter);
            }
        }

        if (virtualFilter) {
            virtualMainFilterParams.push(virtualFilter);
        }
    });

    if (mainFilterParams.length) {
        searchParamObject['mf'] = mainFilterParams.map(stripVirtualMarker).join(';');
    }

    const allQuickFilterParams = quickFilterParams.concat(sharedKeyFilterParams.map(stripVirtualMarker));
    if (allQuickFilterParams.length) {
        searchParamObject['qf'] = allQuickFilterParams.join(';');
    }

    if (searchText.length) {
        searchParamObject['search'] = searchText;
    }

    return { searchParamObject, quickFilterParams, virtualMainFilterParams };
};

/** Whether the URL says what the state does, `sort` included: then the URL change was the list's own write. */
export const compareSearchParams = (
    urlSearchParams: URLSearchParams,
    searchParams: {
        searchText: string;
        quickFilterState: QuickFilterForm;
        mainFilterState: MainFilterForm;
        sort: string;
    }
) => {
    const { searchParamObject } = serializeFilterParams(searchParams);
    if (searchParams.sort) {
        searchParamObject['sort'] = searchParams.sort;
    }
    return isEqual(Object.fromEntries(urlSearchParams), searchParamObject);
};
